// src/components/ContactDeleteConfirmModal/index.js
import React, { useState } from "react";
import clsx from "clsx";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

import { getTokens } from "../../styles/tokens";
import { usePanelStyles, ModalTitle, DangerButton } from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // aviso em destaque: vermelho para "todos", âmbar para "selecionados"
    alert: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 12px",
      marginBottom: 12,
      borderRadius: t.radius.sm,
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.5,
      "& svg": { fontSize: 20, flex: "none", marginTop: 1 },
    },
    alertError: { backgroundColor: t.status.down.bg, color: t.status.down.fg },
    alertWarning: { backgroundColor: t.status.warn.bg, color: t.status.warn.fg },
    message: {
      margin: "0 0 16px",
      fontFamily: t.font,
      fontSize: 14,
      lineHeight: 1.6,
      color: t.mode === "dark" ? t.text : t.label,
    },
    expected: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 12.5,
      fontWeight: 600,
      padding: "1px 6px",
      borderRadius: t.radius.badge,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      color: t.text,
      whiteSpace: "nowrap",
    },
  };
});

const ContactDeleteConfirmModal = ({
  open,
  onClose,
  onConfirm,
  deleteType, // 'selected' ou 'all'
  selectedCount,
  totalCount,
}) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState("");

  const expectedText = deleteType === 'all' ? 'EXCLUIR TODOS' : 'EXCLUIR SELECIONADOS';
  const isConfirmValid = confirmText === expectedText;

  const handleConfirm = () => {
    if (!isConfirmValid) {
      setError(`Digite exatamente: ${expectedText}`);
      return;
    }
    
    setConfirmText("");
    setError("");
    onConfirm();
  };

  const handleClose = () => {
    setConfirmText("");
    setError("");
    onClose();
  };

  const getTitle = () => {
    if (deleteType === 'all') {
      return `Excluir TODOS os ${totalCount} contatos?`;
    }
    return `Excluir ${selectedCount} contatos selecionados?`;
  };

  const getMessage = () => {
    if (deleteType === 'all') {
      return (
        <>
          <div className={clsx(classes.alert, classes.alertError)} role="alert">
            <WarningRoundedIcon />
            <span>
              ATENÇÃO: Esta ação irá excluir TODOS os {totalCount} contatos da empresa permanentemente!
            </span>
          </div>
          <p className={classes.message}>
            Esta operação não pode ser desfeita. Todos os contatos, seus históricos de mensagens, 
            tags e relacionamentos serão perdidos definitivamente.
          </p>
        </>
      );
    }
    
    return (
      <>
        <div className={clsx(classes.alert, classes.alertWarning)} role="alert">
          <WarningRoundedIcon />
          <span>Esta ação irá excluir {selectedCount} contatos selecionados permanentemente.</span>
        </div>
        <p className={classes.message}>
          Os contatos selecionados e seus dados relacionados serão excluídos definitivamente.
        </p>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-confirm-dialog"
      maxWidth="xs"
      fullWidth
      classes={{ paper: p.dialogPaper }}
    >
      <ModalTitle id="delete-confirm-dialog" title={getTitle()} onClose={handleClose} />
      <DialogContent className={clsx(p.modalBody, p.labelsAbove)}>
        {getMessage()}

        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label={
            <>
              Para confirmar, digite exatamente: <span className={classes.expected}>{expectedText}</span>
            </>
          }
          value={confirmText}
          onChange={(e) => {
            setConfirmText(e.target.value);
            setError("");
          }}
          placeholder={expectedText}
          inputProps={{ "aria-label": "Confirmação" }}
          error={!!error}
          helperText={error}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Cancelar
        </Button>
        <DangerButton onClick={handleConfirm} disabled={!isConfirmValid}>
          {deleteType === 'all' ? 'Excluir Todos' : 'Excluir Selecionados'}
        </DangerButton>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDeleteConfirmModal;